import { useRouter } from 'vue-router'

/**
 * Checks if the event indicates the user wants to open in a new tab.
 * This includes ctrl+click (Windows/Linux), cmd+click (Mac), and middle-click.
 */
export function shouldOpenInNewTab(event: MouseEvent): boolean {
  return event.ctrlKey || event.metaKey || event.button === 1
}

/**
 * Navigates to a URL, supporting modifier keys to open in a new tab.
 * - Regular click: navigates in the same tab using vue-router
 * - Ctrl+click (Windows/Linux) or Cmd+click (Mac): opens in new tab
 * - Middle-click: opens in new tab
 */
export function navigateWithModifiers(
  event: MouseEvent,
  url: string,
  routerPush: (url: string) => void
): void {
  if (shouldOpenInNewTab(event)) {
    window.open(url, '_blank')
  } else {
    routerPush(url)
  }
}

/**
 * Composable that provides navigation with modifier key support.
 * Returns a function that can be used as a click handler.
 */
export function useNavigateWithModifiers() {
  const router = useRouter()

  const navigate = (event: MouseEvent, url: string) => {
    navigateWithModifiers(event, url, (url) => router.push(url))
  }

  return {
    navigate,
    shouldOpenInNewTab
  }
}
